import React, { useState, useEffect, useRef } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import axios from "axios";

export default function PhotoCrop({
  pdfDocument,
  pageNumber,
  pdfScale,
  folderName,
  onClose,
}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: "%", width: 30, aspect: 1 }); // square crop
  const [completedCrop, setCompletedCrop] = useState(null);
  const [loading, setLoading] = useState(false);
  const imgRef = useRef(null);
  const canvasRef = useRef(document.createElement("canvas"));

  // Render the PDF page to a canvas and extract as data URL
  useEffect(() => {
    if (!pdfDocument || !pageNumber) return;

    const renderPage = async () => {
      try {
        const page = await pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: pdfScale });

        // Create an off-screen canvas
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
        setImageSrc(canvas.toDataURL()); // data URL for <img>
      } catch (error) {
        console.error("Error rendering PDF page:", error);
      }
    };

    renderPage();
  }, [pdfDocument, pageNumber, pdfScale]);

  // Handle crop complete
  const onCropComplete = (crop) => {
    setCompletedCrop(crop);
  };

  // Upload the cropped image
  const handleUpload = async () => {
    if (!completedCrop || !imgRef.current || !imageSrc) return;

    setLoading(true);

    try {
      // Create a canvas to draw the cropped area
      const canvas = document.createElement("canvas");
      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      canvas.width = completedCrop.width;
      canvas.height = completedCrop.height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width,
        completedCrop.height
      );

      // Convert to base64 (JPEG)
      const base64Image = canvas.toDataURL("image/jpeg");

      // Send to backend
      const response = await axios.post("/api/upload-photo", {
        folderName,
        image: base64Image,
      });

      if (response.data.success) {
        alert("✅ Photo saved successfully!");
        onClose();
      } else {
        alert("❌ Upload failed: " + response.data.message);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("❌ An error occurred while uploading.");
    } finally {
      setLoading(false);
    }
  };

  if (!imageSrc) {
    return (
      <div className="photo-crop-modal">
        <div className="photo-crop-content">
          <h3>Loading PDF page...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="photo-crop-modal">
      <div className="photo-crop-content">
        <h3>Crop Passport Size Photo</h3>
        <div className="crop-container">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={onCropComplete}
            aspect={1}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="PDF Page"
              style={{ maxWidth: "100%", maxHeight: "70vh" }}
            />
          </ReactCrop>
        </div>
        <div className="crop-actions">
          <button onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button onClick={handleUpload} disabled={loading || !completedCrop}>
            {loading ? "Uploading..." : "Save Photo"}
          </button>
        </div>
      </div>
    </div>
  );
}